import { Controller } from "@hotwired/stimulus"
import { Popover } from "bootstrap"

export default class extends Controller {
  static values = { url: String, error: String }

  connect() {
    this.open = false
    this.requested = false
    this.popover = new Popover(this.element, {
      trigger: "manual",
      html: true,
      container: "body",
      placement: "auto",
      customClass: "record-preview-popover",
      content: () => this.content
    })
    this.onOutsideClick = this.handleOutsideClick.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
    this.onOtherPreview = this.handleOtherPreview.bind(this)
    this.onTipLeave = this.scheduleHide.bind(this)
    document.addEventListener("click", this.onOutsideClick)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("record-preview:open", this.onOtherPreview)
  }

  disconnect() {
    clearTimeout(this.showTimer)
    clearTimeout(this.hideTimer)
    this.open = false
    this.requested = false
    this.abortController?.abort()
    document.removeEventListener("click", this.onOutsideClick)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("record-preview:open", this.onOtherPreview)
    this.tip?.removeEventListener("mouseleave", this.onTipLeave)
    this.popover?.dispose()
  }

  scheduleShow() {
    clearTimeout(this.hideTimer)
    clearTimeout(this.showTimer)
    this.showTimer = setTimeout(() => this.show(), 120)
  }

  leave() {
    clearTimeout(this.showTimer)
    this.scheduleHide()
  }

  scheduleHide() {
    clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => {
      if (this.element.matches(":hover") || this.tip?.matches(":hover") || this.element.matches(":focus-visible")) return
      this.hide()
    }, 120)
  }

  blur(event) {
    if (!this.element.contains(event.relatedTarget)) this.hide()
  }

  focus() {
    if (this.element.matches(":focus-visible")) this.show()
  }

  async show() {
    if (this.open) return
    clearTimeout(this.hideTimer)
    this.requested = true
    if (!this.loaded) await this.load()
    if (!this.requested || this.open || (!this.element.matches(":hover") && !this.element.matches(":focus-visible"))) return

    document.dispatchEvent(new CustomEvent("record-preview:open", { detail: { controller: this } }))
    this.open = true
    this.popover.show()
    this.tip?.removeEventListener("mouseleave", this.onTipLeave)
    this.tip = document.getElementById(this.element.getAttribute("aria-describedby"))
    this.tip?.addEventListener("mouseleave", this.onTipLeave)
  }

  async load() {
    if (this.loadingPromise) return this.loadingPromise
    this.abortController = new AbortController()

    this.loadingPromise = (async () => {
      try {
        const response = await fetch(this.urlValue, {
          headers: { Accept: "text/html" },
          signal: this.abortController.signal
        })
        if (!response.ok || response.redirected) throw new Error(`Preview request failed: ${response.status}`)
        this.content = await response.text()
        this.loaded = true
      } catch (_error) {
        this.content = this.errorValue
      } finally {
        this.loadingPromise = null
      }
    })()

    return this.loadingPromise
  }

  handleOutsideClick(event) {
    if ((!this.requested && !this.open) || this.element.contains(event.target)) return
    const popover = document.getElementById(this.element.getAttribute("aria-describedby"))
    if (!popover?.contains(event.target)) this.hide()
  }

  handleKeydown(event) {
    if (this.open && event.key === "Escape") {
      this.hide()
      this.element.focus()
    }
  }

  handleOtherPreview(event) {
    if (event.detail.controller !== this) this.hide()
  }

  hide() {
    clearTimeout(this.showTimer)
    clearTimeout(this.hideTimer)
    this.requested = false
    if (!this.open) return
    this.open = false
    this.tip?.removeEventListener("mouseleave", this.onTipLeave)
    this.popover.hide()
  }
}
